// AuthProvider — top-level context that watches the Supabase session and
// refetches entitlement when:
//   - the session changes (sign-in / sign-out)
//   - the window regains focus (cheap server sync)
//   - the caller explicitly asks (post-checkout redirect, manual refresh)
//
// Components consume via `useAuth()` for the session + `useEntitlement()` for
// the membership/balance/AI flag.

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, SUPABASE_FN_URL } from './supabase';
import type { Entitlement, EntitlementState } from './types';

interface AuthContextValue extends EntitlementState {
  session: Session | null;
  user: User | null;
  signOut: () => Promise<void>;
}

const EMPTY: Entitlement = { membership: null, balance: 0, ai_unlocked: false };

const Ctx = createContext<AuthContextValue | null>(null);

function computeCanEdit(e: Entitlement): boolean {
  const m = e.membership;
  if (!m) return false;
  if (m.status !== 'trial' && m.status !== 'active') return false;
  return new Date(m.period_end).getTime() > Date.now();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [entitlement, setEntitlement] = useState<Entitlement>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef<Promise<void> | null>(null);

  const fetchEntitlement = useCallback(async (s: Session | null): Promise<void> => {
    if (!s) {
      setEntitlement(EMPTY);
      setLoading(false);
      return;
    }
    if (inflight.current) return inflight.current;
    const run = (async () => {
      try {
        setError(null);
        const res = await fetch(`${SUPABASE_FN_URL}/entitlement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${s.access_token}`,
          },
        });
        if (!res.ok) throw new Error(`entitlement HTTP ${res.status}`);
        const data: Entitlement = await res.json();
        setEntitlement(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        // Don't clobber existing data on transient failure; keep last good.
      } finally {
        setLoading(false);
        inflight.current = null;
      }
    })();
    inflight.current = run;
    return run;
  }, []);

  // Boot: read current session, subscribe to changes.
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      void fetchEntitlement(s);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
      void fetchEntitlement(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchEntitlement]);

  // Refresh on window focus — covers Stripe-redirect-back and "the page sat open all day".
  useEffect(() => {
    function onFocus() { void fetchEntitlement(session); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchEntitlement, session]);

  const refresh = useCallback(() => fetchEntitlement(session), [fetchEntitlement, session]);
  const signOut = useCallback(async () => { await supabase.auth.signOut(); }, []);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    user: session?.user ?? null,
    membership: entitlement.membership,
    balance: entitlement.balance,
    ai_unlocked: entitlement.ai_unlocked,
    canEdit: computeCanEdit(entitlement),
    loading,
    error,
    refresh,
    signOut,
  }), [session, entitlement, loading, error, refresh, signOut]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

export function useEntitlement(): EntitlementState {
  const { membership, balance, ai_unlocked, canEdit, loading, error, refresh } = useAuth();
  return { membership, balance, ai_unlocked, canEdit, loading, error, refresh };
}
